import Image from '@/components/ui/image';
import { cn } from '@/lib/utils';

interface EventCompactHeaderProps {
  event: {
    name: string;
    subtitle?: string | null;
    banner_image?: string | null;
  };
  className?: string;
  /** Public draw details need a shorter mobile pre-bracket stack. */
  mobileDense?: boolean;
}

/** Event identity for public sub-pages; each page keeps its own document heading. */
export function EventCompactHeader({
  event,
  className,
  mobileDense = false,
}: EventCompactHeaderProps) {
  const initials = event.name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => Array.from(word)[0])
    .join('')
    .toLocaleUpperCase();

  return (
    <header
      data-event-compact-header
      className={cn('flex min-w-0 items-center gap-3 print:hidden', className)}
    >
      <div
        aria-hidden="true"
        className={cn(
          'bg-card border-border relative shrink-0 overflow-hidden rounded-lg border md:h-14 md:w-14',
          mobileDense ? 'h-8 w-8' : 'h-12 w-12',
        )}
      >
        <span className="text-muted absolute inset-0 flex items-center justify-center text-sm font-semibold">
          {initials}
        </span>
        {event.banner_image && (
          <Image
            key={event.banner_image}
            src={event.banner_image}
            alt=""
            width={56}
            height={56}
            sizes="56px"
            className="bg-card relative h-full w-full object-contain"
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-foreground leading-snug font-semibold break-words md:text-xl',
            mobileDense ? 'text-sm' : 'text-lg',
          )}
        >
          {event.name}
        </p>
        {event.subtitle && (
          <p
            className={cn('text-muted mt-1 text-sm break-words', mobileDense && 'hidden md:block')}
          >
            {event.subtitle}
          </p>
        )}
      </div>
    </header>
  );
}
